// The browser's sign-in flow (02-DESIGN/01-identity/03-signing-in.md § the
// browser): the authorization code flow with PKCE against the issuer the
// profile names, a `state` checked on return, a `nonce` the ID token must
// echo, the code exchanged in the page or through the same-origin endpoint,
// and refresh by the same path. The page holds no session on any server.

import { followable } from "../address/address.ts";
import type { PlatformProfile, Provider, TenantProfile } from "../platform/profile.ts";
import { challenge, newNonce, newState, newVerifier } from "./pkce.ts";
import { browserClientId, type Choice } from "./provider.ts";
import { savePending, takePending, type Exchange, type Pending, type Probe, type Session } from "./tokens.ts";

/** SignInError is why a sign-in did not complete. */
export class SignInError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "SignInError";
  }
}

/** RefreshRefused says the refresh token was refused: the person signs in again. */
export class RefreshRefused extends SignInError {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "RefreshRefused";
  }
}

/** redirectUri is the console's callback: its own origin's neutral page. */
export function redirectUri(origin: string): string {
  return new URL("/", origin).toString();
}

/**
 * providerOf is the provider a session was signed in with: the tenant's own
 * where its issuer is the session's, the platform's otherwise.
 */
export function providerOf(
  platform: PlatformProfile,
  tenant: TenantProfile | null,
  session: Session,
): Provider {
  const own = tenant?.provider;
  if (own !== undefined && own.issuer !== undefined && own.issuer === session.issuer) {
    return own;
  }
  return platform.platform_provider;
}

/**
 * endSessionUrl is where sign-out sends the browser after forgetting the
 * tokens, to end the issuer's own session as well (RP-initiated logout,
 * 03-signing-in.md § the browser): the end-session endpoint with the ID token
 * as the hint, the client id, and the neutral page to return to. Undefined
 * where the provider names no end-session endpoint — the github lane, an
 * issuer that advertises none or one that is not https — and forgetting the
 * tokens is the whole sign-out.
 */
export function endSessionUrl(origin: string, provider: Provider, session: Session): string | undefined {
  const endpoint = provider.end_session_endpoint;
  if (session.lane !== "oidc" || endpoint === undefined || !followable(endpoint)) {
    return undefined;
  }
  const u = new URL(endpoint);
  u.searchParams.set("id_token_hint", session.credential);
  u.searchParams.set("post_logout_redirect_uri", redirectUri(origin));
  const clientId = browserClientId(provider);
  if (clientId !== "") {
    u.searchParams.set("client_id", clientId);
  }
  return u.toString();
}

/** DEFAULT_SCOPES are asked of an OpenID Connect provider that lists none. */
export const DEFAULT_SCOPES = ["openid", "profile", "email", "offline_access"];

/**
 * begin mints the flow's state, nonce and verifier, keeps them beside the
 * address to return to, and returns the authorization URL to navigate to.
 */
export async function begin(
  origin: string,
  choice: Choice,
  returnTo: string,
  probe?: Probe,
): Promise<string> {
  const endpoint = choice.provider.authorization_endpoint;
  if (endpoint === undefined || endpoint === "") {
    throw new SignInError("the provider names no authorization endpoint");
  }
  if (!followable(endpoint)) {
    throw new SignInError("the provider's authorization endpoint is not https");
  }
  if (choice.clientId === "") {
    throw new SignInError("the provider names no client id for the browser");
  }
  const state = newState();
  const nonce = newNonce();
  const verifier = newVerifier();
  const pending: Pending = {
    state,
    nonce,
    verifier,
    lane: choice.lane,
    slug: choice.slug,
    issuer: choice.provider.issuer ?? "",
    exchange: choice.exchange,
    returnTo,
    begun: Date.now(),
  };
  if (probe !== undefined) {
    pending.probe = probe;
  }
  savePending(pending);
  const u = new URL(endpoint);
  u.searchParams.set("client_id", choice.clientId);
  u.searchParams.set("redirect_uri", redirectUri(origin));
  u.searchParams.set("state", state);
  if (choice.lane === "oidc") {
    u.searchParams.set("response_type", "code");
    u.searchParams.set("scope", (choice.provider.scopes ?? DEFAULT_SCOPES).join(" "));
    u.searchParams.set("nonce", nonce);
    u.searchParams.set("code_challenge", await challenge(verifier));
    u.searchParams.set("code_challenge_method", "S256");
  } else if (choice.provider.scopes !== undefined && choice.provider.scopes.length > 0) {
    u.searchParams.set("scope", choice.provider.scopes.join(" "));
  }
  return u.toString();
}

/** TokenReply is what a token endpoint — the provider's or the auth service's — answers. */
export interface TokenReply {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
  error_description?: string;
}

async function post(exchange: Exchange, form: Record<string, string>, f: typeof fetch): Promise<TokenReply> {
  const body = new URLSearchParams(form);
  if (exchange.kind === "public" && exchange.clientId !== undefined) {
    body.set("client_id", exchange.clientId);
  } else if (exchange.kind === "server-side") {
    // The auth service picks the client id registered for this surface (impire-auth/internal/web/web.go).
    body.set("surface", "browser");
  }
  let resp: Response;
  try {
    resp = await f(exchange.url, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded", accept: "application/json" },
      body: body.toString(),
    });
  } catch (err) {
    throw new SignInError(
      `the token endpoint could not be reached: ${err instanceof Error ? err.message : String(err)}`,
      {
        cause: err,
      },
    );
  }
  let reply: TokenReply;
  try {
    reply = (await resp.json()) as TokenReply;
  } catch (err) {
    throw new SignInError(`the token endpoint answered ${resp.status} and no JSON`, { cause: err });
  }
  if (!resp.ok || reply.error !== undefined) {
    throw new SignInError(
      `the token endpoint answered ${resp.status}: ${reply.error ?? ""} ${reply.error_description ?? ""}`.trim(),
    );
  }
  return reply;
}

/** Claims are the ID token's claims the page reads: the nonce it must echo, the issuer, and a display. */
export interface Claims {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  exp?: number;
  nonce?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  login?: string;
}

/** decodeClaims reads an ID token's payload without verifying it: the auth service verifies; the page checks the echo. */
export function decodeClaims(idToken: string): Claims {
  const parts = idToken.split(".");
  if (parts.length !== 3 || parts[1] === undefined) {
    throw new SignInError("the ID token is not a JWT");
  }
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (parts[1].length % 4)) % 4);
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      bytes[i] = bin.charCodeAt(i);
    }
    return JSON.parse(new TextDecoder().decode(bytes)) as Claims;
  } catch (err) {
    throw new SignInError("the ID token's claims cannot be read", { cause: err });
  }
}

function sessionOf(
  pending: Pick<Pending, "lane" | "slug" | "issuer" | "exchange">,
  reply: TokenReply,
  previous?: Session,
): Session {
  const now = Date.now();
  const expiresAt = reply.expires_in !== undefined ? now + reply.expires_in * 1000 : undefined;
  const base: Session = {
    lane: pending.lane,
    slug: pending.slug,
    issuer: pending.issuer,
    credential: "",
    exchange: pending.exchange,
  };
  const refresh = reply.refresh_token ?? previous?.refreshToken;
  if (refresh !== undefined) {
    base.refreshToken = refresh;
  }
  if (pending.lane === "github") {
    if (reply.access_token === undefined || reply.access_token === "") {
      throw new SignInError("the exchange answered no access token");
    }
    base.credential = reply.access_token;
    if (expiresAt !== undefined) {
      base.expiresAt = expiresAt;
    }
    if (previous?.display !== undefined) {
      base.display = previous.display;
    }
    return base;
  }
  if (reply.id_token === undefined || reply.id_token === "") {
    throw new SignInError("the exchange answered no ID token");
  }
  const claims = decodeClaims(reply.id_token);
  if (pending.issuer !== "" && claims.iss !== pending.issuer) {
    throw new SignInError(`the ID token is from "${claims.iss ?? ""}", not the provider the profile names`);
  }
  base.credential = reply.id_token;
  const exp = claims.exp !== undefined ? claims.exp * 1000 : undefined;
  const until = [exp, expiresAt].filter((v): v is number => v !== undefined);
  if (until.length > 0) {
    base.expiresAt = Math.min(...until);
  }
  const display =
    claims.name ?? claims.preferred_username ?? claims.email ?? claims.login ?? previous?.display;
  if (display !== undefined) {
    base.display = display;
  }
  return base;
}

/**
 * complete reads the code and state off the return URL, checks the state
 * against the flow begun, exchanges the code, checks the nonce echo, and
 * returns the session and the address to return to.
 */
export async function complete(
  origin: string,
  url: URL,
  f: typeof fetch = fetch,
): Promise<{ session: Session; returnTo: string; probe?: Probe }> {
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const error = url.searchParams.get("error");
  if (error !== null) {
    throw new SignInError(
      `the provider answered "${error}": ${url.searchParams.get("error_description") ?? ""}`.trim(),
    );
  }
  if (code === null || state === null) {
    throw new SignInError("the return carries no code and state");
  }
  const pending = takePending(state);
  if (pending === undefined) {
    throw new SignInError("the return's state matches no sign-in this page began, or that sign-in is stale");
  }
  const reply = await post(
    pending.exchange,
    {
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri(origin),
      code_verifier: pending.verifier,
    },
    f,
  );
  const session = sessionOf(pending, reply);
  if (pending.lane === "oidc") {
    const claims = decodeClaims(session.credential);
    if (claims.nonce !== pending.nonce) {
      throw new SignInError("the ID token does not echo the nonce this page sent");
    }
  }
  return pending.probe !== undefined
    ? { session, returnTo: pending.returnTo, probe: pending.probe }
    : { session, returnTo: pending.returnTo };
}

/**
 * refresh trades the refresh token for a new credential by the same path as
 * the exchange. A refusal means the person signs in again.
 */
export async function refresh(session: Session, f: typeof fetch = fetch): Promise<Session> {
  if (session.refreshToken === undefined || session.refreshToken === "") {
    throw new RefreshRefused("no refresh token: sign in again");
  }
  try {
    const reply = await post(
      session.exchange,
      { grant_type: "refresh_token", refresh_token: session.refreshToken },
      f,
    );
    // An answer without a usable credential is a refusal too: the person signs in again.
    return sessionOf(session, reply, session);
  } catch (err) {
    throw new RefreshRefused(err instanceof Error ? err.message : String(err), { cause: err });
  }
}

/** expiresSoon reports whether the credential expires within the window, in milliseconds. */
export function expiresSoon(session: Session, window: number, now = Date.now()): boolean {
  return session.expiresAt !== undefined && session.expiresAt - now <= window;
}
